import { logger } from '../../../utils/core/logger';
import type { FormDetectionContext } from '../types';

/**
 * GTM dataLayer events that always indicate a successful conversion, regardless
 * of per-client config. HubSpot's standard GTM event for the embed-components-app
 * iframe is `hubspot-form-success` — used by the newer hsforms.net hosted forms
 * which do NOT emit postMessage events.
 *
 * Docs: https://knowledge.hubspot.com/forms/google-tag-manager
 */
const BUILT_IN_EVENTS: Record<string, string> = {
    'hubspot-form-success': 'hubspot',
};

type DataLayerPushArgs = unknown[];
type DataLayerPush = (...args: DataLayerPushArgs) => number;

interface DataLayerWindow extends Window {
    dataLayer?: unknown[] & { push: DataLayerPush };
}

/**
 * Per-client dataLayer conversion event spec.
 *
 * - Plain string: match by `event` name only (legacy shape).
 * - Object: match `event` name AND require every key in `where` to be present
 *   and equal on the pushed dataLayer object. Use when one event name is
 *   reused for multiple conversion types and only a sibling property (commonly
 *   `eventAction`) distinguishes them — e.g. abtasty pushes the same
 *   `formSubmissionSuccess` event for both newsletter and demo submissions,
 *   differing only by `eventAction`.
 */
export type DataLayerConversionEvent = string | { event: string; where?: Record<string, string> };

interface NormalizedEvent {
    event: string;
    where: Record<string, string>;
}

/**
 * Strategy 8: DataLayer Detection
 *
 * Hooks `window.dataLayer.push` and fires `rw_client_form_submitted` when a
 * configured event name appears. Like PostMessageStrategy, this is a global
 * listener with no per-form eligibility gate — the event name itself is the
 * signal. False positives are controlled by the explicit allowlist of event
 * names (built-in + per-client configured).
 *
 * Re-hooking is idempotent: if a third party reassigns `window.dataLayer`
 * after we hook, we re-hook on each push. We also flag our wrapper so we
 * never double-wrap.
 */
export class DataLayerStrategy {
    name = 'data_layer';
    private isActive = false;
    private clientEvents: Map<string, NormalizedEvent[]>;
    private detectedEvents = new Set<string>();
    private trackedKeys = new Map<string, number>();
    private originalPush: DataLayerPush | null = null;
    private hookedArray: unknown[] | null = null;
    private dataLayerValue: (unknown[] & { push: DataLayerPush }) | undefined;
    private dataLayerDescriptor: PropertyDescriptor | undefined;
    private dataLayerAccessorInstalled = false;
    private readonly DEDUP_WINDOW_MS = 2000;

    constructor(clientEvents: DataLayerConversionEvent[] = []) {
        this.clientEvents = new Map();
        for (const entry of clientEvents) {
            const normalized = this.normalizeEntry(entry);
            if (!normalized) continue;
            const bucket = this.clientEvents.get(normalized.event) ?? [];
            // Warn when the same event name is registered with both an
            // empty-where (plain-string) spec and a constrained (object) spec.
            // `Array.find` returns the first match and an empty `where`
            // trivially matches every push, so the constrained spec would
            // never be evaluated — almost certainly a config mistake.
            const hasEmptyWhere = bucket.some((s) => Object.keys(s.where).length === 0);
            const addingConstrained = Object.keys(normalized.where).length > 0;
            const addingEmpty = Object.keys(normalized.where).length === 0;
            const hasConstrained = bucket.some((s) => Object.keys(s.where).length > 0);
            if ((hasEmptyWhere && addingConstrained) || (hasConstrained && addingEmpty)) {
                logger.warn(
                    `📊 DataLayerStrategy: event "${normalized.event}" registered with both ` +
                        `unconstrained (plain-string) and constrained (where-clause) specs. ` +
                        `The unconstrained spec matches every push first, so where-clauses ` +
                        `for this event will never apply. Remove the plain-string entry.`
                );
            }
            bucket.push(normalized);
            this.clientEvents.set(normalized.event, bucket);
        }
    }

    private normalizeEntry(entry: unknown): NormalizedEvent | null {
        if (typeof entry === 'string' && entry.length > 0) {
            return { event: entry, where: {} };
        }
        if (entry && typeof entry === 'object') {
            const obj = entry as { event?: unknown; where?: unknown };
            if (typeof obj.event !== 'string' || obj.event.length === 0) return null;
            const where: Record<string, string> = {};
            if (obj.where && typeof obj.where === 'object') {
                for (const [k, v] of Object.entries(obj.where as Record<string, unknown>)) {
                    if (typeof v === 'string') {
                        where[k] = v;
                    } else {
                        logger.warn(
                            `📊 DataLayerStrategy: dropped non-string where[${k}] for ` +
                                `event "${obj.event}" (got ${typeof v}). ` +
                                `Predicate keys must be strings — config likely mistyped.`
                        );
                    }
                }
            }
            return { event: obj.event, where };
        }
        return null;
    }

    private whereMatches(where: Record<string, string>, obj: Record<string, unknown>): boolean {
        for (const [k, v] of Object.entries(where)) {
            if (obj[k] !== v) return false;
        }
        return true;
    }

    checkAndActivate(context: FormDetectionContext): boolean {
        if (this.isActive) return true;

        const w = window as DataLayerWindow;
        if (!Array.isArray(w.dataLayer)) {
            // Seed dataLayer so we can hook before any third-party push.
            w.dataLayer = [] as unknown as unknown[] & { push: DataLayerPush };
        }
        this.installDataLayerAccessor(w, context);
        this.hookPush(w, context);

        // Replay any events that were already in the queue when we attached.
        if (w.dataLayer && w.dataLayer.length > 0) {
            for (const entry of w.dataLayer) {
                this.inspectEntry(entry, context);
            }
        }

        this.isActive = true;
        logger.debug('📊 DataLayerStrategy: hooked window.dataLayer.push', {
            builtInEvents: Object.keys(BUILT_IN_EVENTS),
            clientEvents: Array.from(this.clientEvents.entries()).map(([name, specs]) => ({
                event: name,
                whereVariants: specs.map((s) => s.where),
            })),
        });
        return true;
    }

    private installDataLayerAccessor(w: DataLayerWindow, context: FormDetectionContext): void {
        if (this.dataLayerAccessorInstalled) return;

        const descriptor = Object.getOwnPropertyDescriptor(w, 'dataLayer');
        if (descriptor && (!descriptor.configurable || !('value' in descriptor))) return;

        this.dataLayerDescriptor = descriptor;
        this.dataLayerValue = w.dataLayer;
        Object.defineProperty(w, 'dataLayer', {
            configurable: true,
            enumerable: descriptor?.enumerable ?? true,
            get: () => this.dataLayerValue,
            set: (value: unknown) => {
                this.dataLayerValue = Array.isArray(value)
                    ? (value as unknown[] & { push: DataLayerPush })
                    : undefined;
                this.hookPush(w, context);
            },
        });
        this.dataLayerAccessorInstalled = true;
    }

    private hookPush(w: DataLayerWindow, context: FormDetectionContext): void {
        const dl = w.dataLayer;
        if (!dl) return;
        const existingPush = dl.push as DataLayerPush & { __rwHooked?: boolean };
        if (existingPush.__rwHooked) {
            this.hookedArray = dl;
            return;
        }
        const original = existingPush;
        const wrapped: DataLayerPush & { __rwHooked?: boolean } = function (
            this: unknown[],
            ...args: DataLayerPushArgs
        ) {
            return original.apply(this, args);
        }.bind(dl) as DataLayerPush;
        // Wrap with inspection on the bound function
        const observed: DataLayerPush & { __rwHooked?: boolean } = (...args: DataLayerPushArgs) => {
            for (const entry of args) {
                try {
                    this.inspectEntry(entry, context);
                } catch (err) {
                    logger.warn('📊 DataLayerStrategy: inspect failed', err);
                }
            }
            return wrapped(...args);
        };
        observed.__rwHooked = true;
        try {
            dl.push = observed;
        } catch (err) {
            // A host (or crawler) can freeze dataLayer; throwing here would abort
            // analytics init and hide the widget (ROSE-FRONT-1ZB).
            logger.warn('📊 DataLayerStrategy: dataLayer.push is read-only, skipping hook', err);
            return;
        }
        this.originalPush = existingPush;
        this.hookedArray = dl;
    }

    private inspectEntry(entry: unknown, context: FormDetectionContext): void {
        if (!entry || typeof entry !== 'object') return;
        const obj = entry as Record<string, unknown>;
        const eventName = typeof obj.event === 'string' ? obj.event : null;
        if (!eventName) return;

        const builtInProvider = BUILT_IN_EVENTS[eventName];
        const clientSpecs = this.clientEvents.get(eventName) ?? [];
        // For client specs we require a where-clause match. A spec with empty
        // `where` (plain-string entry) trivially matches everything with this
        // event name, preserving legacy behaviour.
        const matchedClientSpec = clientSpecs.find((spec) => this.whereMatches(spec.where, obj));
        if (!builtInProvider && !matchedClientSpec) {
            // Debug-only visibility into every dataLayer event we decided to
            // ignore — without this, a conversion event with an unexpected
            // name (or a failing where-clause) is indistinguishable from no
            // event at all when diagnosing missed conversions.
            logger.debug('📊 DataLayerStrategy: ignoring unmatched event', { eventName });
            return;
        }

        if (!this.detectedEvents.has(eventName)) {
            this.detectedEvents.add(eventName);
            logger.debug(`📊 DataLayerStrategy: matched event ${eventName}`, {
                provider: builtInProvider ?? 'custom',
                whereClause: matchedClientSpec?.where ?? null,
            });
        }

        const provider = builtInProvider ?? 'datalayer';
        const extractedFormId = this.extractFormId(obj);
        // When the dataLayer payload exposes a real form identifier (HubSpot's
        // `hubspotFormId`, generic `formId`/`form_id`, etc.) we expose it so
        // downstream gating (form_id_whitelist) can apply. Otherwise fall back
        // to the event name as a synthetic id for dedup only.
        const formId = extractedFormId ?? eventName;
        // IX-4411: built-in provider events (HubSpot's `hubspotFormId` GUID) used
        // to be exempt from `form_id_whitelist` on the grounds that a GUID never
        // matches an operator's DOM-style whitelist entries. But the server has
        // always gated on any form id it receives, so the exemption only meant
        // those forms shipped as `rw_client_form_submitted` and were read as
        // conversions by consumers reading PostHog directly, while every Rose
        // conversion count already excluded them. A real extracted id is a real
        // identity whatever produced it; the synthetic event-name fallback is a
        // dedup label and stays out of the gate.
        const applyFormIdWhitelist = extractedFormId !== null;
        // Include matched where-clause in dedupKey so two variants of the
        // same event name (e.g. eventAction=Demo vs eventAction=Newsletter)
        // do not collapse into one detection within the 2s window.
        const whereDiscriminator = matchedClientSpec?.where
            ? Object.entries(matchedClientSpec.where)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([k, v]) => `${k}=${v}`)
                  .join('&')
            : '';
        const dedupKey = `${provider}:${formId}:${eventName}:${whereDiscriminator}`;
        const now = Date.now();
        for (const [key, ts] of this.trackedKeys) {
            if (now - ts > this.DEDUP_WINDOW_MS) this.trackedKeys.delete(key);
        }
        if (this.trackedKeys.has(dedupKey)) {
            logger.debug('📊 DataLayerStrategy: skipping duplicate', { dedupKey });
            return;
        }
        this.trackedKeys.set(dedupKey, now);

        const indicators = [`datalayer_event:${eventName}`];
        if (builtInProvider) indicators.push(`provider_${builtInProvider}`);

        logger.debug('📊 DataLayerStrategy: form submission detected', {
            provider,
            eventName,
            formId,
        });

        context.onFormSubmit({
            formId,
            pageUrl: context.currentUrl,
            detectionMethod: this.name,
            provider,
            status: 'success',
            indicators,
            applyFormIdWhitelist,
        });
    }

    private extractFormId(obj: Record<string, unknown>): string | null {
        // Form-specific keys only. `obj.id` is intentionally excluded: it is a
        // common property on GA4 enhanced-ecommerce pushes, custom-dimension
        // pushes, and many third-party tag templates (trigger/product/sequence
        // ids), so treating any non-empty `id` as a "real form identifier"
        // would unexpectedly subject those events to `form_id_whitelist`.
        const candidates = [
            obj.formId,
            obj.form_id,
            obj.hubspotFormId,
            obj['hs-form-guid'],
            obj.hsFormGuid,
        ];
        for (const c of candidates) {
            if (typeof c === 'string' && c.length > 0) return c;
        }
        return null;
    }

    cleanup(): void {
        if (this.hookedArray && this.originalPush) {
            (this.hookedArray as { push: DataLayerPush }).push = this.originalPush;
        }
        if (this.dataLayerAccessorInstalled) {
            Object.defineProperty(window, 'dataLayer', {
                configurable: true,
                enumerable: this.dataLayerDescriptor?.enumerable ?? true,
                writable: true,
                value: this.dataLayerValue,
            });
        }
        this.hookedArray = null;
        this.originalPush = null;
        this.dataLayerValue = undefined;
        this.dataLayerDescriptor = undefined;
        this.dataLayerAccessorInstalled = false;
        this.detectedEvents.clear();
        this.trackedKeys.clear();
        this.isActive = false;
        logger.debug('📊 DataLayerStrategy: cleaned up');
    }
}
